/**
 * Get URL parameter value in a case-insensitive manner
 * @param params - URLSearchParams object
 * @param paramName - The parameter name to look for
 * @returns The parameter value or null if not found
 */
export function getCaseInsensitiveParam(
    params: URLSearchParams,
    paramName: string
): string | null {
    // First try the exact case
    let value = params.get(paramName);
    if (value !== null) {
        return value;
    }

    // Try different case variations first (for performance)
    const variations = [
        paramName.toLowerCase(),
        paramName.toUpperCase(),
        paramName.charAt(0).toUpperCase() + paramName.slice(1).toLowerCase(), // Title case
    ];

    for (const variation of variations) {
        value = params.get(variation);
        if (value !== null) {
            return value;
        }
    }

    // If still not found, iterate through all parameters (case-insensitive)
    const targetParamLower = paramName.toLowerCase();
    const entries = Array.from(params.entries());
    for (const [key, val] of entries) {
        if (key.toLowerCase() === targetParamLower) {
            return val;
        }
    }

    return null;
}

/**
 * Get multiple URL parameter values in a case-insensitive manner
 * @param params - URLSearchParams object
 * @param paramNames - Array of parameter names to try
 * @returns The first found parameter value or null if none found
 */
export function getCaseInsensitiveParamFromList(
    params: URLSearchParams,
    paramNames: string[]
): string | null {
    for (const paramName of paramNames) {
        const value = getCaseInsensitiveParam(params, paramName);
        if (value !== null) {
            return value;
        }
    }
    return null;
}
